<template>
    <div class="c-swiper c-swiper--horizontal">
        <swiper :options="swiperOption" ref="swiper" v-on="$listeners">
            <slot name="default"><!-- --></slot>
            <template v-if="showButton !== 'none'">
              <div :id="`${uniqId}_next`" class="c-swiper__button c-swiper__button--next" :class="{'c-swiper__button--arrow': showButton === 'arrow', 'c-swiper__button--circle': showButton === 'circle'}" slot="button-next"></div>
              <div :id="`${uniqId}_prev`" class="c-swiper__button c-swiper__button--prev" :class="{'c-swiper__button--arrow': showButton === 'arrow', 'c-swiper__button--circle': showButton === 'circle'}" slot="button-prev"></div>
            </template>
            <div :id="`${uniqId}_pagination`" v-if="hasCounter" slot="pagination" class="c-swiper__bullet"></div>
        </swiper>
        <slot name="gallery" :items="items"><!-- Slot for a PhotoSwipe gallery --></slot>
    </div>
</template>
<script lang="ts">
import throttle from 'lodash/throttle';
import { Component, Prop } from 'vue-property-decorator';
import { dataLayer } from '../globals';
import { EventBus } from '../helpers/bus';
import BaseComponent from './Base';
import Swiper from './Swiper.vue';
import { ISwiperComponent, Slide } from './SwiperTypes';

@Component({
  components: {
    Swiper,
  },
})
export default class HorizontalSwiper extends BaseComponent {
  public readonly $refs: { swiper: any };

  @Prop({ type: Object, default: () => ({}) }) protected readonly options: { [key: string]: any };
  @Prop({ type: Number, default: 0 }) protected readonly slideWidth: number;
  @Prop({ type: [String, Number, Function], default: 'auto' }) protected readonly slidesPerView: any;
  @Prop({ type: Boolean, default: true }) protected readonly preventClick: boolean;
  @Prop({ type: Boolean, default: false }) protected readonly isAdaptive: boolean;
  @Prop({ type: Boolean, default: false }) protected readonly hasCounter: boolean;
  @Prop({ type: String, default: 'arrow' }) protected readonly showButton: 'arrow' | 'circle' | 'none';
  @Prop({ type: String, default: 'Horizontal Swiper' }) protected readonly gtmCategory: string;

  protected items: any[] = [];
  protected isVisible: boolean = false;
  protected swiperOption: any = {};

  protected get swiper(): ISwiperComponent {
    return (this.$refs.swiper as ISwiperComponent);
  }

  protected init(): any {
    if (this.isVisible || this.$el.offsetParent === null) {
      return;
    }
    this.isVisible = true;
    Array.from(this.swiper.getSlides()).forEach((slide: Slide, index) => {
      const link: HTMLAnchorElement | null = (slide as HTMLElement).querySelector('a');

      // It's ugly fix for ugly solution.
      // webp, jpg, jpeg, gif, png
      if (link === null
          || (!['.webp', '.jpg', '.jpeg', '.png', '.gif'].find((item) => link.href.includes(item)) && !link.classList.contains('c-tile--video'))
      ) {
        return;
      }

      // TODO Image sizes issue
      const src: string = link ? (link.href || '') : '';
      if (!link.classList.contains('c-tile--video')) {
        const item: any = {
          src,
          title: (slide as HTMLElement).dataset.title,
          initialPosition: { x: 0, y: 0 },
        };
        this.items.push(item);
        const img = new Image();
        img.addEventListener('load', function () {
          item.w = this.naturalWidth;
          item.h = this.naturalHeight;
        });
        img.src = src;
      } else {
        this.items.push({
          src: '',
          html: '<div class="u-video-center"><div class="pswp__video-container"><div is="youtube-video" class="pswp__video" src="' + link.href + '"></div></div></div>',
        });
      }
      link.addEventListener('click', (event) => {
        dataLayer.push({
          eventAction: 'Slide click',
          eventCategory: this.gtmCategory,
          event: 'horizontalSwiper:slideClick',
        });
        this.$emit('slideClick', this.items, index, slide, this.swiper, event);
        if (this.preventClick) {
          event.preventDefault();
        }
      });
    });
    if (this.slideWidth > 0 && this.slidesPerView !== 'auto') {
      this.onResize({ width: this.$el.offsetWidth });
      EventBus.$on('windowResize', () => this.onResize({ width: this.$el.offsetWidth }));
    }

    this.swiper.update();
  }

  /**
   * This function must be triggered only if there is a number of slides set.
   *
   * @param {number} width  Parent element width
   */
  protected onResize({ width }: { width: number }): void {
    const canFit = Math.max(1, Math.min(this.slidesPerView, Math.round(width / this.slideWidth)));
    if (this.swiperOption.slidesPerView === canFit) {
      return;
    }
    this.swiperOption.slidesPerView = canFit;
    this.swiper.setSlidesPerView(canFit);
  }

  protected beforeMount(): void {
    this.swiperOption = {
      initialSlide: 0,
      watchOverflow: true,
      simulateTouch: false,
      spaceBetween: this.slideWidth > 0 || this.slidesPerView > 1 ? 20 : 0,
      ...this.options,
      slidesPerView: this.slidesPerView,
      autoHeight: this.isAdaptive,
      navigation: {
        nextEl: `#${this.uniqId}_next`,
        prevEl: `#${this.uniqId}_prev`,
      },
      pagination: {
        el: `#${this.uniqId}_pagination`,
        clickable: true,
      },
    };
  }

  protected mounted(): void {
    this.init();
    EventBus.$on('updateSwipers', throttle(this.init, 150, { leading: false }));
    this.$refs.swiper.$on('slideChange', () => dataLayer.push({
      eventAction: 'Slide change',
      eventCategory: this.gtmCategory,
      event: 'horizontalSwiper:slideChange',
    }));
  }
}
</script>
